import {defineStore} from 'pinia';
import {examinationToExaminationTemplate} from "../Utils/examinationUtil";
import {comparisonMethods} from "../Composables/examinationTemplate";
import {textWithVariables} from "../VueComponents/Suggestion.vue";
import {toValue} from "vue";

/**
 * @typedef {Object} initialRecords
 * @property {array} records
 * @property {string} lastImported
 */

/**
 * @typedef {Object} examination
 */

/**
 * @return {Promise<initialRecords>}
 */
async function fetchInitialRecords(initialUrl) {
    return fetch(initialUrl)
        .then(response => response.json())
        .catch(reason => {
            reject(reason);
        });
}

export const examinationTemplateStoreFactory = async (initialUrl) => {
    const initialRecords = await fetchInitialRecords(initialUrl);

    return defineStore('examinationTemplates', {
        state: () => {
            return {...initialRecords, activeFilters: []}
        },
        getters: {
            filteredRecords(state) {
                return state.records.filter((examinationTemplate) => {
                    const activeFilters = state.filters.filter(filter => filter.enabled);
                    return !activeFilters.map(filter => filter.predicate(examinationTemplate)).includes(true)
                });
            },
            knoopIdFilters(state) {
                return [
                    {
                        low: '[;1000.00.0]',
                        high: '[;2000.00.0]',
                        name: '1000er',
                        __identity: 'justThe1000erKnoopIdFilter'
                    },
                    {
                        low: '[;2000.00.0]',
                        high: '[;2100.00.0]',
                        name: '2000er',
                        __identity: 'justThe2000erKnoopIdFilter'
                    },
                    {
                        low: '[;2100.00.0]',
                        high: '[;2200.00.0]',
                        name: '2100er',
                        __identity: 'justThe2100erKnoopIdFilter'
                    },
                    {
                        low: '[;3000.00.0]',
                        high: '[;4000.00.0]',
                        name: '3000er',
                        __identity: 'justThe3000erKnoopIdFilter'
                    },
                    {
                        low: '[;5000.00.0]',
                        high: '[;6000.00.0]',
                        name: '5000er',
                        __identity: 'justThe5000erKnoopIdFilter'
                    },
                ];
            },
            filters(state) {
                const filters = [];
                state.knoopIdFilters.forEach(knoopIdFilter => {
                    const id = `knoopIdFilter_${knoopIdFilter.__identity}`;
                    const enabled = state.activeFilters.includes(id);
                    filters.push({
                        id,
                        enabled,
                        type: 'hardCodedKnoopId',
                        name: knoopIdFilter.name,
                        predicate: (examinationTemplate) => knoopIdFilter.low <= examinationTemplate.knoopId
                            && examinationTemplate.knoopId < knoopIdFilter.high,
                    });
                });
                return filters;
            },
        }
    });
};

export function getAllExaminationTemplates(siteInspection, databaseRecords) {
    const knownDescriptions = databaseRecords.records
        .map(examination => examination.description);
    const localExaminations = [];
    const knownExaminationTexts = [];
    for (const examination of toValue(siteInspection).examinations) {
        const examinationText = textWithVariables(examination.deficiency.text, examination.variables);
        if (
            (examination.dirty === undefined || examination.edited === true)
            && (
                !knownDescriptions.includes(examination.deficiency.text)
                || comparisonMethods.isFreeText(examination, {examinationTemplates: databaseRecords.records})
            )
            && !knownExaminationTexts.includes(examinationText)
        ) {
            knownExaminationTexts.push(examinationText);
            localExaminations.push(examinationToExaminationTemplate(examination));
        }
    }
    return localExaminations.concat(databaseRecords.filteredRecords);
}
